import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withState, withProps, withMethods, patchState, withComputed, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Router } from '@angular/router';
import { pipe, switchMap } from 'rxjs';
import { PaymentsService } from '../../services/payments.service';
import { DictionaryService } from '../../services/dictionary.service';
import { AppParametersDto } from '../../Models/AppParametersDto';
import { PostPayByLinkRequest } from '../../Models/Requests/PostPayByLinkRequest';
import { CaseDto } from '../../Models/CaseDto';
import { CaseService } from '../../services/case.service';
import { NotificationService } from '../../services/notification.service';
import { PaymentParamsDto } from '../../Models/PaymentParamsDto';

export interface PaymentState {
  params: AppParametersDto[],
  paymentLink: string,
  caseDto: CaseDto,
  isError: boolean,
  isLoading: boolean,
  amountToPay: number,
  amountToPayAsString: string,
}

export interface PaymentLinkInput {
    caseHashId: string;
    amount: number;
}

const initialPaymentState : PaymentState = {
  params: [],
  paymentLink: '',
  caseDto: null!,
  isError: false,
  isLoading: false,
  amountToPay: 0,
  amountToPayAsString: '0',
};

export const PaymentStore = signalStore(
  { providedIn: 'root' },
  withState(initialPaymentState),
  withProps(() => ({
    paymentService: inject(PaymentsService),
    caseService: inject(CaseService),
    dictionaryService: inject(DictionaryService),
    router: inject(Router),
    notificationService: inject(NotificationService),
  })),
  withComputed(({ caseDto: caseDto }) => ({
    minAmountForPayment: computed(() => {
      const result = caseDto()?.minPaymentByLinkAmount ?? 0;
      return result;
    }),
    maxAmountForPayment: computed(() => {
      const result = caseDto()?.balance ?? 0;
      return result;
    }),
    defaultAmount: computed(() => {
      const nextPayment = caseDto()?.nextInstallmentAmount;
      const caseBalance = caseDto()?.balance;
      const defaultAmount = nextPayment ?? caseBalance;
      return defaultAmount ?? 0;
    }),
  })),
  withMethods(({ paymentService, router, dictionaryService, caseService, notificationService, ...store }) => ({
    getPaymentLink: rxMethod<PostPayByLinkRequest>(
      pipe(
        switchMap((input: PostPayByLinkRequest) => {
          patchState(store, { isLoading: true });
          return paymentService.getLinkForPayment(input).pipe(
            tapResponse({
              next: (linkResult) => {
                window.location.href = linkResult.link;
                patchState(store, { paymentLink: linkResult.link, isLoading: false });
                return;
              },
              error: () => {
                patchState(store, { isLoading: false });
                notificationService.error('notification.error-first-version');
                return;
              },
            }),
          );
        }),
      ),
    ),
    getAppParams: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { isLoading: true });
          return dictionaryService.getAppParams().pipe(
            tapResponse({
              next: (appParamsResult) => patchState(store, { params: appParamsResult.params, isLoading: false }),
              error: () => {
                patchState(store, { isLoading: false });
                notificationService.error('notification.error-first-version');
                return;
              },
            }),
          );
        }),
      ),
    ),
    loadData: rxMethod<PaymentParamsDto>(
      pipe(
        switchMap((paymentParamsDto) => {
          patchState(store, { isLoading: true });
          return caseService.getCaseDetails(paymentParamsDto.caseHashId).pipe(
            tapResponse({
              next: (caseResult) => {
                const caseDto = caseResult.casesDto;
                const defaultAmount = (caseDto.nextInstallmentAmount ?? caseDto.balance) ?? 0;
                const calculatePaymentAmount = paymentParamsDto.isMultiPay ? (paymentParamsDto.defaultAmount ?? 0) : defaultAmount;
                if(caseDto.balance === 0 || caseDto.minPaymentByLinkAmount === 0 || calculatePaymentAmount === 0) {
                  router.navigate(['']);
                  return;
                }

                const calculatePaymentAmountRounded = Number(calculatePaymentAmount.toFixed(2));
                return patchState(store, { caseDto: caseResult.casesDto, amountToPay: calculatePaymentAmountRounded, amountToPayAsString: calculatePaymentAmountRounded.toString().replace('.', ',') });
              },
              error: () => {
                notificationService.error('notification.error-first-version');
                return;
              },
              finalize: () => patchState(store, { isLoading: false }),
            }),
          );
        }),
      ),
    ),
    setAmountToPay(amount: number): void {
      patchState(store, { amountToPay: amount, amountToPayAsString: amount.toString().replace('.', ',') });
    },
    navigateToDashboard(): void {
      router.navigate(['']);
    },
    navigateToCaseDetails(id: string): void {
      router.navigate(['case-details'], { state: { caseHashId: id } });
    },
    navigateToOrderContact(): void {
      router.navigate(['/order-contact']);
    },
  })),
  withHooks({
    onInit(store) {
      store.getAppParams();
    },
  }),
);
